import React from 'react';
import { BedDoubleIcon, CalendarCheckIcon, ChevronRightIcon } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import { useUi } from '../contexts/UiContext';
import { DEMO_DATE } from '../data/demo';
import { formatDayLabel } from '../utils/time';
import { namesOf } from '../utils/members';
import { currentSlot, dutySlots } from '../utils/duties';
import { PageHeader } from '../components/ui/PageHeader';
import { Panel } from '../components/ui/Panel';
import { ApprovalQueue } from '../components/review/ApprovalQueue';
import { OpenAgreements } from '../components/review/OpenAgreements';
import { ProgressRing } from '../components/ProgressRing';
import { Avatar } from '../components/Avatar';
export function OverviewPage() {
  const {
    family,
    activeMembers,
    weekFor,
    approvalsCount,
    viewer,
    reviewQueue
  } = useAppState();
  const {
    openSub
  } = useUi();
  const kids = activeMembers.filter(m => m.role === 'otrok');
  const todayEvents = family.events.filter(e => e.date === DEMO_DATE).sort((a, b) => a.start.localeCompare(b.start));
  return <div data-id="element-1509" data-mp="2uljbz9ot9hh8">
      <PageHeader title="Pregled" subtitle={<span className="capitalize" data-id="element-1511" data-mp="2uk87f7njk5p2">
            {formatDayLabel(DEMO_DATE)} · {approvalsCount > 0 ? `${approvalsCount} zadev čaka nate, ${viewer.name}` : 'nič ne čaka nate'}
          </span>} data-id="element-1510" data-mp="2ult2qzhojsnz" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8" data-id="element-1512" data-mp="3ibnhxnph1tbe">
        <div className="min-w-0 space-y-6" data-id="element-1513" data-mp="3i9azzajpriyc">
          <Panel title={`Za potrditev${approvalsCount ? ` · ${approvalsCount}` : ''}`} hint="Opravila, nagrade in izjeme – vsako dejanje je ločeno." data-id="element-1514" data-mp="3e9sg8i3mpdx3">
            <ApprovalQueue data-id="element-1515" data-mp="39d1xfn0fw47z" />
          </Panel>

          {kids.length > 0 && <Panel title="Člani ta teden" hint="Osnovni plan glede na osebni načrt. Tapni za podrobnosti." data-id="element-1516" data-mp="34bmeq1lqi9sr">
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5" data-id="element-1517" data-mp="1xtpi3zth8ou1">
                {kids.map(m => {
              const w = weekFor(m.id);
              const pending = reviewQueue.filter(t => t.assigneeIds.includes(m.id)).length;
              return <li key={m.id} data-id="element-1518" data-mp="1zc6b27ge59hj">
                      <button type="button" onClick={() => openSub({
                  kind: 'member',
                  id: m.id
                })} className="flex w-full flex-col items-center gap-2 rounded-3xl bg-canvas p-3 text-center transition-colors duration-150 hover:bg-line/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-1519" data-mp="1ldgdyrtkn9t6">
                        <ProgressRing pct={w.pct} threshold={w.threshold} size={72} label={`${m.name}: ${w.pct} %`} data-id="element-1520" data-mp="12n8fwo4qb9ju" />
                        <span className="flex items-center gap-1.5 font-bold" data-id="element-1521" data-mp="16nihzppehftf">
                          <span className="h-2.5 w-2.5 rounded-full" style={{
                      backgroundColor: m.color
                    }} aria-hidden="true" data-id="element-1522" data-mp="snzcz5rvyklc" />
                          {m.name}
                        </span>
                        <span className="text-xs text-muted" data-id="element-1523" data-mp="e1k6l0dz6uov">{w.excusedWeek ? 'oproščen teden' : w.reached ? 'prag dosežen' : `manjka ${w.missingPct} %`}{pending ? ` · ${pending} v pregledu` : ''}</span>
                      </button>
                    </li>;
            })}
              </ul>
            </Panel>}
        </div>

        <aside className="space-y-4" data-id="element-1524" data-mp="izlp9k7horzt">
          <button type="button" onClick={() => openSub({
          kind: 'dutyReview'
        })} className="flex w-full items-center gap-3 rounded-3xl bg-sun-soft p-4 text-left transition-colors duration-150 hover:bg-sun/30" data-id="element-1525" data-mp="x0emicp6e16m">
            <BedDoubleIcon className="h-6 w-6 shrink-0 text-sun-ink" aria-hidden="true" data-id="element-1526" data-mp="16r0vho2dztgc" />
            <span className="min-w-0 flex-1" data-id="element-1527" data-mp="11roaz9yzmygx">
              <span className="block font-display text-base font-semibold text-sun-ink" data-id="element-1528" data-mp="11rwnv1v423s4">Pregled zadolžitev</span>
              <span className="block text-xs text-sun-ink" data-id="element-1529" data-mp="1frfhkpn6dckw">
                Naslednji: {dutySlots.find(s => s.slot === currentSlot())?.label} · hiša in sobe
              </span>
            </span>
            <ChevronRightIcon className="h-4 w-4 text-sun-ink" aria-hidden="true" data-id="element-1530" data-mp="3581bqwapbj6e" />
          </button>
          {family.modules.nagrade && <button type="button" onClick={() => openSub({
          kind: 'weekClose'
        })} className="flex w-full items-center gap-3 rounded-3xl bg-done-soft p-4 text-left transition-colors duration-150 hover:bg-done/20" data-id="element-1531" data-mp="3eyctpmr7zswa">
              <CalendarCheckIcon className="h-6 w-6 shrink-0 text-done-ink" aria-hidden="true" data-id="element-1532" data-mp="2ma8jqp1m8tns" />
              <span className="min-w-0 flex-1" data-id="element-1533" data-mp="2r80dloltmpum">
                <span className="block font-display text-base font-semibold text-done-ink" data-id="element-1534" data-mp="2r8s4wn8dy1f3">Tedenski zaključek</span>
                <span className="block text-xs text-done-ink" data-id="element-1535" data-mp="2w9zf0sy03cox">
                  {family.rules.payoutDay} {family.rules.payoutTime} · žepnina in zaklep tedna
                </span>
              </span>
              <ChevronRightIcon className="h-4 w-4 text-done-ink" aria-hidden="true" data-id="element-1536" data-mp="bjqm5aarpmnh" />
            </button>}
          <Panel title="Odprti dogovori" data-id="element-1537" data-mp="200s196ozsoap">
            <OpenAgreements data-id="element-1538" data-mp="200i9peoau92w" />
          </Panel>
          <Panel title="Danes v koledarju" data-id="element-1539" data-mp="1vzey54daaz7h">
            {todayEvents.length === 0 ? <p className="text-sm text-muted" data-id="element-1540" data-mp="1w1sqh5gkxxbc">Prazen dan.</p> : <ul className="space-y-2" data-id="element-1541" data-mp="1r6lpi5ep36q4">
                {todayEvents.map(e => <li key={e.id} className="flex gap-3 text-sm" data-id="element-1542" data-mp="1m93nkyz3hrrm">
                    <span className="w-12 shrink-0 font-extrabold tabular-nums" data-id="element-1543" data-mp="1hasfotsd1rc1">{e.start}</span>
                    <span className="min-w-0 flex-1" data-id="element-1544" data-mp="1cb62ulgg2d3r">
                      <span className="block font-bold" data-id="element-1545" data-mp="1cdjtmi43d35i">{e.title}</span>
                      <span className="block text-xs text-muted" data-id="element-1546" data-mp="18adt7r3nu66y">{e.participantIds.length ? namesOf(family, e.participantIds) : 'Vsi'}</span>
                    </span>
                  </li>)}
              </ul>}
          </Panel>
          <div className="flex items-center gap-2 px-1 text-xs text-muted" data-id="element-1547" data-mp="2xp1v2yemk5qq">
            <Avatar member={viewer} size="sm" data-id="element-1548" data-mp="2xmxsqkwdjesk" /> Svoje naloge najdeš v zavihku Danes.
          </div>
        </aside>
      </div>
    </div>;
}